/**
 * "The item matching ladder" (docs section 4): the four rungs of `resolve_line` for one
 * line (L0, L0b, L1, L2) with the read-only tools of each rung, the auto-accept rule for
 * a pick below L0, and the six steps that run after the ladder. Four of them have an
 * environment switch (13 Oct 2026: the verifier, the calibrated gate, the investigator
 * with its refute step, pre-flight; each default is off). On a laptop the rule stands
 * beside the ladder; on a phone it stands under it (the narrow media query).
 */

import { ArrowDown, ArrowRight, Icon, ShieldCheck, ToggleRight } from "@norican/icons";
import { Badge, Card, Timeline, type TimelineItem, Typography } from "@norican/react";
import type { ReactNode } from "react";

import { NARROW_QUERY, useMediaQuery } from "@/hooks/use-media-query";

import { Figure } from "./figure";

export const ITEM_MATCHING_LADDER_TITLE = "The item matching ladder";

/** What the two parts of the drawing show, for a screen reader. */
export const RUNGS_SUMMARY = "The four rungs that find the item of one line, in order";
export const RULE_SUMMARY = "The rule for an automatic accept, and the steps after the ladder";

/** A rung: its level in the code, its name, what it does, and the tools it calls. */
type Rung = readonly [level: string, title: string, text: string, tools: readonly string[]];

/** The rungs of `resolve_line`. A rung without an item hands the line to the next rung. */
export const ITEM_RUNGS: readonly Rung[] = [
  [
    "L0",
    "Exact number, alias, rule",
    "An exact item number comes first. Then a stored alias, then a rule for a token with the shape of an item number.",
    ["tool_exact_code", "tool_alias"],
  ],
  [
    "L0b",
    "Old item number",
    "A discontinued item number with one replacement gives the replacement.",
    ["tool_supersede"],
  ],
  [
    "L1",
    "Candidates",
    "The BOM of owned machines, the purchase history, semantic search and text search give a list.",
    ["tool_bom_candidates", "tool_history_candidates", "tool_semantic", "tool_search"],
  ],
  [
    "L2",
    "Model pick",
    "The model picks one candidate from the list, or it abstains.",
    ["tool_has_bought", "tool_in_bom"],
  ],
];

export const RULE_TITLE = "Automatic accept of a model pick";
export const RULE_BADGE = "auto-accept";
// ste-allow: code
export const RULE_CODE = "auto = bought and conf >= 0.85 and not sibling and not sem_only";

/** The four facts of the rule: the term in the code, then the fact that must be true. */
export const RULE_FACTS: readonly (readonly [string, string])[] = [
  ["bought", "The customer bought the item before."],
  ["conf >= 0.85", "The model gives 0.85 or more to its pick."],
  ["not sibling", "The customer did not also buy an item with an almost equal item number."],
  ["not sem_only", "Semantic search is not the only source of the item."],
];
export const RULE_ELSE = "If one fact is missing, the pick is a suggestion for a sales person.";
export const RULE_MAIL_PATH =
  "The mail path is stricter: a model pick is always a suggestion there (hold `model_pick`). It accepts only exact item numbers, aliases and successors.";

export const AFTER_TITLE = "After the ladder";
/**
 * The steps after the per-line ladder, in order: the name, the module, what it does, and
 * the environment switch that gates it (a step without one always runs).
 */
export const AFTER_STAGES: readonly (readonly [string, string, string, string?])[] = [
  [
    "Learned corrections",
    "matching/learned.py",
    "An exact earlier correction for the same customer replaces the item.",
  ],
  [
    "Sources for spare parts",
    "spare/lines.py",
    "Five sources add or change lines. Each such line is a suggestion.",
  ],
  [
    "Verifier",
    "spare/verifier.py",
    "One model call checks the proposed items. It keeps or removes an automatic accept.",
    "RFQ_SPARE_VERIFIER",
  ],
  [
    "Calibrated gate",
    "calibrate/live.py",
    "A second opinion gives each line a calibrated score. In the mode `gate` a line below its threshold becomes a suggestion.",
    "RFQ_CALIBRATION_MODE",
  ],
  [
    "Investigator and refute step",
    "matching/investigation/agent.py",
    // Round 2 (13 Oct 2026): the alternative beside a matched item, and the revised quotation.
    "A loop with read-only tools looks for the item of an open line. A refute step checks each proposal. A proposal is a suggestion. A replacement is shown beside the written item, and the line keeps its item.",
    "RFQ_MATCHING_INVESTIGATION_MODE",
  ],
  [
    "Pre-flight",
    "preflight/live.py",
    "It checks each item, price and quantity against the master data. A finding is a rule flag.",
    "RFQ_PREFLIGHT",
  ],
];
export const AFTER_BADGE = "environment switch";
export const AFTER_NOTE =
  "A step with this badge runs only for the entities that its variable names. Each default is off. The verifier and the gate can remove an automatic accept. By default, no step of the four adds one.";

export const RUNGS_NOTE =
  "The ladder is `resolve_line` in `backend/src/rfq_foundry/engine/pipeline/resolve.py`. Every tool only reads the mirror.";

const BOX = "flex min-w-0 flex-col gap-2";

/** Backtick spans in code font, as the documentation page prints them. */
function code(text: string): ReactNode {
  return text.split("`").map((part, index) =>
    index % 2 ? (
      <Typography key={index} as="code" variant="code" className="text-[0.9em] break-words">
        {part}
      </Typography>
    ) : (
      part
    ),
  );
}

/** `layout` overrides the media query for the render tests. Omit it in the app. */
export function ItemMatchingLadderFigure(props: {
  caption?: string;
  layout?: "beside" | "stacked";
}) {
  const narrow = useMediaQuery(NARROW_QUERY);
  const form = props.layout ?? (narrow ? "stacked" : "beside");
  const stacked = form === "stacked";
  const rungs: TimelineItem[] = ITEM_RUNGS.map(([level, title, text, tools]) => ({
    id: level,
    tone: "accent",
    title: (
      <span className="flex flex-wrap items-center gap-1.5">
        <Badge tone="accent" variant="solid" size="sm" className="font-mono">
          {level}
        </Badge>
        {title}
      </span>
    ),
    description: (
      <span className="flex min-w-0 flex-col items-start gap-1">
        <span className="break-words">{text}</span>
        <span className="flex min-w-0 flex-wrap gap-1">
          {tools.map((tool) => (
            <Badge key={tool} tone="neutral" variant="outline" size="sm" shape="rounded">
              <span className="font-mono">{tool}</span>
            </Badge>
          ))}
        </span>
      </span>
    ),
  }));
  return (
    <Figure name="item-matching-ladder" title={ITEM_MATCHING_LADDER_TITLE} caption={props.caption}>
      <div
        data-layout={form}
        className={stacked ? BOX : "grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-3"}
      >
        <Timeline
          orientation="vertical"
          connector="arrow"
          items={rungs}
          aria-label={RUNGS_SUMMARY}
        />
        <span className="text-fg-muted flex flex-col items-center justify-center text-xs">
          pick
          <Icon icon={stacked ? ArrowDown : ArrowRight} size="sm" label="to" />
        </span>
        <div role="group" aria-label={RULE_SUMMARY} className={BOX}>
          <Card variant="outline" padding="sm" accent="left" accentTone="success" className={BOX}>
            <span className="flex min-w-0 flex-wrap items-center gap-1.5">
              <Icon icon={ShieldCheck} size="sm" tone="success" className="shrink-0" />
              <Typography variant="label">{RULE_TITLE}</Typography>
              <Badge tone="success" variant="subtle" size="sm">
                {RULE_BADGE}
              </Badge>
            </span>
            <Typography as="code" variant="code" className="text-[0.85em] break-words">
              {RULE_CODE}
            </Typography>
            <ul className="flex flex-col gap-1">
              {RULE_FACTS.map(([term, fact]) => (
                <li key={term} className="flex min-w-0 flex-col text-sm">
                  <Typography as="code" variant="code" className="text-[0.85em]">
                    {term}
                  </Typography>
                  <span className="break-words">{fact}</span>
                </li>
              ))}
            </ul>
            <Typography variant="caption" tone="muted" className="break-words">
              {RULE_ELSE}
            </Typography>
            <Typography variant="caption" className="break-words">
              {code(RULE_MAIL_PATH)}
            </Typography>
          </Card>
          <Card variant="outline" padding="sm" className={BOX}>
            <Typography variant="label">{AFTER_TITLE}</Typography>
            {AFTER_STAGES.map(([name, path, text, gate]) => (
              <span key={name} className="flex min-w-0 flex-col items-start gap-0.5 text-sm">
                <span className="font-semibold">{name}</span>
                <span className="break-words">{code(text)}</span>
                <Typography
                  as="code"
                  variant="code"
                  tone="muted"
                  className="text-[0.8em] break-all"
                >
                  {path}
                </Typography>
                {gate && (
                  <span className="flex min-w-0 flex-wrap items-center gap-1.5" data-gate={gate}>
                    <Badge tone="warning" variant="outline" size="sm">
                      <Icon icon={ToggleRight} size="xs" />
                      {AFTER_BADGE}
                    </Badge>
                    <Typography as="code" variant="code" className="text-[0.8em] break-all">
                      {gate}
                    </Typography>
                  </span>
                )}
              </span>
            ))}
            <Typography variant="caption" tone="muted" className="break-words">
              {AFTER_NOTE}
            </Typography>
          </Card>
        </div>
      </div>
      <Typography variant="caption" tone="muted" className="break-words">
        {code(RUNGS_NOTE)}
      </Typography>
    </Figure>
  );
}
